import axios from "axios";
import { useEffect, useState } from "react";
import { useAuth } from "../Auths/CheckUser";
import MainLayout from "../layout/MainLayout";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const StaffProfile = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    status: "",
    propertyNo: "",
  });

  // Fetch staff details
  useEffect(() => {
    if (!user || !user.id) return;

    const fetchData = async () => {
      try {
        const res = await axios.get(`https://bpohasil.ngrok.app/api/user/${user.id}`);
        setData(res.data);
        setFormData({
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          status: res.data.status || "",
          propertyNo: res.data.propertyNo || "",
        });
      } catch (error) {
        console.error("Error fetching staff profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  // Handle form submit (update user)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://bpohasil.ngrok.app/api/user/${user.id}`, formData);
      Swal.fire("Success", "Profile updated successfully!", "success");
      setEditMode(false);
      // Refresh updated data
      const res = await axios.get(`https://bpohasil.ngrok.app/api/user/${user.id}`);
      setData(res.data);
    } catch (error) {
      console.error("Error updating user:", error);
      Swal.fire("Error", "Failed to update profile.", "error");
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 text-center text-gray-600">Loading profile...</div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="p-6 text-center text-red-500">
          Failed to load staff data.
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              Staff Profile
            </h2>
            <p className="text-gray-500 text-sm">Staff ID: {data.id}</p>
          </div>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Edit
            </button>
          )}
        </div>

        {/* Edit Mode Form */}
        {editMode ? (
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Edit Profile
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter name"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter phone number"
                  pattern="[0-9]{10,15}"
                  required
                />
              </div>

              {/* Property Number */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Property No
                </label>
                <input
                  type="text"
                  name="propertyNo"
                  value={formData.propertyNo}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter property number"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select status</option>
                  <option value="On Duty">On Duty</option>
                  <option value="Off Duty">Off Duty</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        ) : (
          // Display Profile
          <div className="bg-white p-6 rounded-xl shadow-md space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Name</h3>
              <p className="text-gray-800">{data.name || "N/A"}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">Email</h3>
              <p className="text-gray-800">{data.email || "N/A"}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">Phone</h3>
              <p className="text-gray-800">{data.phone || "N/A"}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Property No
              </h3>
              <p className="text-gray-800">{data.propertyNo || "N/A"}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">Status</h3>
              <p
                className={`${
                  data.status === "On Duty"
                    ? "text-green-600 font-medium"
                    : "text-red-500 font-medium"
                }`}
              >
                {data.status || "N/A"}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">Role</h3>
              <p className="text-gray-800 capitalize">{data.role || "N/A"}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Account Created
              </h3>
              <p className="text-gray-800">
                {data.createdAt
                  ? new Date(data.createdAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Last Updated
              </h3>
              <p className="text-gray-800">
                {data.updatedAt
                  ? new Date(data.updatedAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>

            <Link to="/list">
              <div className="cursor-pointer mt-3">
                <h3 className="text-lg font-semibold text-gray-700">
                  Total Assigned Sewa Clients
                </h3>
                <p className="text-blue-600 font-bold text-xl">
                  { data.clients.length ?? 0}
                </p>
              </div>

              <div className="cursor-pointer mt-3">
                <h3 className="text-lg font-semibold text-gray-700">
                  Total Assigned Air Clients
                </h3>
                <p className="text-blue-600 font-bold text-xl">
                  { data.airclients.length ?? 0}
                </p>
              </div>
            </Link>

            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Total Arrears
              </h3>
              <p className="text-red-600 font-bold text-xl">
                {data.arreas.length ?? 0}
              </p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default StaffProfile;
